// ABOUTME: The "Export shortlist" button: saves the shortlisted candidates shown as the CSV the employer gets, and tracks it.
// ABOUTME: Employers see it where canExportShortlist allows; staff see it on every shortlist, as a preview (debugging).
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CandidateData } from '$types/candidates';
import PositionInternalDto from '$types/serverTypes/positionInternalDto';
import { getTenantDefaultFee } from '$constants/SimeraOperations';
import { ReactComponent as Export } from '$icons/export.svg';
import { useAppSelector } from '../../../../hooks';
import { buildShortlistCsv } from './shortlistCsv';
import { trackEvent } from '../../../../utils/trackEvent';

const ExportShortlistCsv = ({
  position,
  candidates,
  disabled,
  viewer,
}: {
  position: PositionInternalDto;
  candidates: CandidateData[];
  /** Set while candidates are loading, so a partial list isn't exported */
  disabled?: boolean;
  /** Staff preview the employer's file; their exports are tracked apart */
  viewer?: 'employer' | 'internal';
}) => {
  const [t] = useTranslation('global');
  const { currencyCode, usdToCurrencyConverter } = useAppSelector(
    (state) => state.brand
  );

  const exportShortlist = useCallback(() => {
    const csv = buildShortlistCsv(candidates, {
      position,
      // The same fee SPKEmployerCandidatesList gives the cards
      fee: position.FeeAmount ?? getTenantDefaultFee(position.Tenant?.Name),
      currencyCode,
      usdToCurrency: usdToCurrencyConverter,
      profileUrl: (id) => `${import.meta.env.REACT_APP_SPK_URL}/spk/${id}`,
    });
    const blobUrl = URL.createObjectURL(
      new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    );
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `Simera shortlist - position ${position.Id} - ${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(blobUrl);
    // After the download, never before it: tracking can't delay or block the file.
    trackEvent('shortlist_csv.exported', {
      positionId: position.Id,
      companyId: position.Company?.Id ?? null,
      rows: candidates.length,
      viewer: viewer ?? 'employer',
      enabledForCompany: position.Company?.ShortlistCsvExport === true,
    });
  }, [candidates, position, currencyCode, usdToCurrencyConverter, viewer]);

  return (
    <button
      type="button"
      className="flex items-center gap-2 bg-employer-main text-white text-sm px-4 py-2 rounded-md disabled:opacity-50"
      onClick={exportShortlist}
      disabled={disabled}
    >
      <Export className="fill-white w-4 rotate-180" />
      {viewer === 'internal'
        ? t('PositionCard.ExportShortlistEmployerView')
        : t('PositionCard.ExportShortlist')}
      {viewer === 'internal' && (
        <span className="text-xs opacity-80">
          (
          {position.Company?.ShortlistCsvExport
            ? t('PositionCard.ShortlistCsvOnForCompany')
            : t('PositionCard.ShortlistCsvOffForCompany')}
          )
        </span>
      )}
    </button>
  );
};

ExportShortlistCsv.defaultProps = {
  disabled: false,
  viewer: 'employer',
};

export default ExportShortlistCsv;
